/**
 * Public leaderboard — unauthenticated users can browse verified lift rankings.
 * Indexable by search engines; conversion path for visitors → sign up.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getTopLifts } from '../../domain/usecases/leaderboard';
import { userRepo } from '../../data/repositories/UserRepository';
import { setSeo } from '../../shared/utils/seo';
import Card from '../../shared/ui/Card.jsx';
import Avatar from '../../shared/ui/Avatar.jsx';
import Badge from '../../shared/ui/Badge.jsx';
import Button from '../../shared/ui/Button.jsx';
import Display from '../../shared/ui/Display.jsx';
import Title from '../../shared/ui/Title.jsx';
import Body from '../../shared/ui/Body.jsx';
import Label from '../../shared/ui/Label.jsx';
import MedalGlyph from '../../shared/ui/MedalGlyph.jsx';
import Chip from '../../shared/ui/Chip.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import styles from './PublicLeaderboardPage.module.css';

const LIFTS = [
  { slug: 'back-squat', label: 'Back Squat' },
  { slug: 'front-squat', label: 'Front Squat' },
  { slug: 'bench-press', label: 'Bench Press' },
  { slug: 'deadlift', label: 'Deadlift' },
  { slug: 'overhead-press', label: 'Overhead Press' },
  { slug: 'clean', label: 'Clean' },
  { slug: 'snatch', label: 'Snatch' },
];

const DEFAULT_LIFT = 'back-squat';

function slugToLabel(slug) {
  return LIFTS.find((l) => l.slug === slug)?.label || null;
}

export function PublicLeaderboardPage() {
  const { lift: liftSlug } = useParams();
  const navigate = useNavigate();
  const slug = liftSlug && LIFTS.some((l) => l.slug === liftSlug) ? liftSlug : DEFAULT_LIFT;
  const liftLabel = slugToLabel(slug);

  const [items, setItems] = useState(null);
  const [users, setUsers] = useState({});

  // SEO
  useEffect(() => {
    const canonical = `https://barchain.com/lb/${slug}`;
    setSeo({
      title: `${liftLabel} Leaderboard — BarChain`,
      description: `Top verified ${liftLabel} lifts on BarChain. Lift, log, and rank your strength.`,
      ogTitle: `${liftLabel} Leaderboard — BarChain`,
      ogDescription: `The strongest verified ${liftLabel} on BarChain. Join the ranks.`,
      ogType: 'website',
      ogImage: 'https://barchain-24c66.web.app/logo.png',
      canonical,
      robots: 'index,follow',
    });
  }, [slug, liftLabel]);

  // Fetch top lifts + batch user profiles for display-name/avatar
  useEffect(() => {
    let cancelled = false;
    setItems(null);
    getTopLifts(liftLabel, 50)
      .then(async (lifts) => {
        if (cancelled) return;
        setItems(lifts);
        const uids = [...new Set(lifts.map((l) => l.userId).filter(Boolean))];
        const docs = await Promise.all(uids.map((id) => userRepo.getById(id).catch(() => null)));
        if (cancelled) return;
        const map = {};
        uids.forEach((id, i) => { if (docs[i]) map[id] = docs[i]; });
        setUsers(map);
      })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [liftLabel]);

  const podium = useMemo(() => {
    if (!items || items.length < 3) return null;
    // Order visually: 2nd, 1st, 3rd
    return [items[1], items[0], items[2]];
  }, [items]);

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <span className={styles.brand}>Bar<span className={styles.brandDot}>Chain</span></span>
        <div className={styles.topActions}>
          <Button variant="ghost" size="sm" as={Link} to="/login">Sign in</Button>
          <Button variant="primary" size="sm" as={Link} to="/signup">Create account</Button>
        </div>
      </div>

      <section className={styles.hero}>
        <Label>Verified leaderboard</Label>
        <Display as="h1" size="xl" style={{ marginTop: 4 }}>{liftLabel}</Display>
        <Body tone="secondary" style={{ marginTop: 8 }}>
          The strongest verified {liftLabel.toLowerCase()} lifts on BarChain. Lift, log, get verified by the community, and stake your rank.
        </Body>
      </section>

      <section className={styles.filters}>
        <Label style={{ marginBottom: 8, display: 'block' }}>Pick a lift</Label>
        <div className={styles.chips}>
          {LIFTS.map((l) => (
            <Chip
              key={l.slug}
              selected={l.slug === slug}
              onClick={() => navigate(`/lb/${l.slug}`)}
            >
              {l.label}
            </Chip>
          ))}
        </div>
      </section>

      {items === null ? (
        <div className={styles.skelList}>
          {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} width="100%" height={64} radius="var(--radius-md)" />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon="trophy"
          title="No ranked lifts yet"
          body={`Be the first to log a verified ${liftLabel}.`}
          action={<Button variant="primary" as={Link} to="/signup">Sign up</Button>}
        />
      ) : (
        <>
          {podium && (
            <div className={styles.podium}>
              {podium.map((entry, i) => {
                const rank = i === 0 ? 'silver' : i === 1 ? 'gold' : 'bronze';
                const user = users[entry.userId];
                return (
                  <div key={entry.id} className={`${styles.podiumCell} ${styles[rank]}`}>
                    <MedalGlyph name="rank" rank={rank} size="lg" />
                    <span className={styles.podiumName}>{user?.displayName || 'Anonymous'}</span>
                    <span className={styles.podiumWeight}>{entry.weight} kg</span>
                  </div>
                );
              })}
            </div>
          )}

          <Title as="h2" className={styles.sectionTitle}>Top 50</Title>
          <Card padding="none" className={styles.listCard}>
            {items.map((entry, i) => {
              const user = users[entry.userId];
              const rank = i + 1;
              const rankClass = rank === 1 ? styles.gold : rank === 2 ? styles.silver : rank === 3 ? styles.bronze : '';
              return (
                <div key={entry.id} className={styles.row}>
                  <span className={`${styles.rank} ${rankClass}`}>{rank}</span>
                  <Avatar size="sm" src={user?.avatarUrl || user?.photoURL} initials={(user?.displayName || '?')[0]} />
                  <div className={styles.rowMeta}>
                    <span className={styles.name}>{user?.displayName || 'Anonymous'}</span>
                    <span className={styles.handle}>@{user?.handle || '—'}</span>
                  </div>
                  <span className={styles.weight}>{entry.weight} kg{entry.reps ? ` × ${entry.reps}` : ''}</span>
                  {entry.verified ? <Badge variant="gold">Verified</Badge> : null}
                </div>
              );
            })}
          </Card>

          <section className={styles.cta}>
            <Title as="h2" style={{ textAlign: 'center', marginBottom: 8 }}>Lift your way onto the board</Title>
            <Body tone="secondary" style={{ textAlign: 'center', marginBottom: 16 }}>
              Log a {liftLabel.toLowerCase()} with a short video. Get 20 verifications from the community. Rank.
            </Body>
            <div className={styles.ctaRow}>
              <Button variant="primary" size="lg" as={Link} to="/signup">Create an account</Button>
              <Button variant="secondary" size="lg" as={Link} to="/login">Sign in</Button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
